import { type Role } from "./role";

/** The app-level user profile (not credentials — those live in company-auth).
 * `firstName` + `lastName` are the canonical name parts; `name` is a
 * denormalized display string ("Anna Schmidt") kept for surfaces that
 * just need one. `company` is the customer organisation the recruiter
 * works for — null for admins.
 *
 * `isSuperuser` mirrors Django's stock flag (set by `createsuperuser`)
 * and gates user-deletion in the UI. Regular admins (role=admin,
 * isSuperuser=false) can invite + manage but cannot remove users.
 *
 * Search quota (non-admins only): admins assign `searchQuota` and see
 * `searchesUsed`; each sourcing run a recruiter starts consumes one.
 * `searchesRemaining` (derived, server-authoritative) gates the UI.
 * Admins are unlimited and never consume. */
export interface User {
  id: string;
  email: string;
  role: Role;
  name: string;
  firstName: string;
  lastName: string;
  company: string | null;
  createdAt: string;
  isSuperuser: boolean;
  searchQuota: number;
  searchesUsed: number;
  searchesRemaining: number;
  /** Release Note ids this user has seen in "What's new" / been told
   * about by a Release Notice (ADR 0005). null until first set. */
  seenWhatsNew: string[] | null;
  notifiedWhatsNew: string[] | null;
}

export function userInitials(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? "").join("") || "?";
}
